// Model loading for sites: the manifest's GLBs reduced to one attribute layout, merged into one
// geometry per instanced slot (a monument is its plinth plus its building: one draw call).
//
//   manifest.json ─► asset id ─► first variant's file ─► GLTFLoader ─► {position, normal, color, _team, _glow}
//   parts (geometry, offset, yaw, scale) ─► compose() ─► one geometry
//
// Attribute meaning follows public/assets/manifest.json: `color` is linear albedo, `_team` a 0..1
// mask (here: the tint mask, painted gold on monuments and in the ore colour on heaps) and `_glow`
// a 0..1 emissive mask.

import * as THREE from 'three/webgpu';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

const ATTRS = ['position', 'normal', 'color', '_team', '_glow'] as const;
const NORMAL_EPSILON = 1e-6;

/** The part of manifest.json sites reads. */
interface Manifest {
  assets: Record<string, { variants: Array<{ file: string }> }>;
}

/** One model placed inside a composed geometry. Offsets and scale are in model units. */
export interface Part {
  geometry: THREE.BufferGeometry;
  x?: number;
  y?: number;
  z?: number;
  yaw?: number;
  scale?: number;
  /** Replaces the part's `_team` mask (undefined keeps the model's own). */
  mask?: number;
}

/** Load the first variant of each asset id; an id that fails is left out (logged, not fatal). */
export async function loadAssets(base: string, ids: readonly string[]): Promise<Map<string, THREE.BufferGeometry>> {
  const out = new Map<string, THREE.BufferGeometry>();
  const res = await fetch(`${base}/manifest.json`);
  if (!res.ok) {
    console.warn(`sites: manifest.json: HTTP ${res.status}`);
    return out;
  }

  const manifest = (await res.json()) as Manifest;
  const loader = new GLTFLoader();
  await Promise.all(ids.map(async (id) => {
    const file = manifest.assets[id]?.variants[0]?.file;
    if (!file) {
      console.warn(`sites: no asset ${id}`);
      return;
    }

    try {
      const gltf = await loader.loadAsync(`${base}/${file}`);
      out.set(id, normalise(firstGeometry(gltf.scene)));
    } catch (err) {
      console.warn(`sites: ${id}: ${String(err)}`);
    }
  }));
  return out;
}

function firstGeometry(root: THREE.Object3D): THREE.BufferGeometry {
  let found: THREE.BufferGeometry | null = null;
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!found && mesh.isMesh) {
      found = mesh.geometry;
    }
  });
  if (!found) {
    throw new Error('no mesh');
  }
  return found;
}

/** Non-indexed geometry with exactly the ATTRS layout: missing masks are 0, missing colour white. */
function normalise(source: THREE.BufferGeometry): THREE.BufferGeometry {
  const g = source.index ? source.toNonIndexed() : source.clone();
  const n = g.getAttribute('position').count;
  for (const name of Object.keys(g.attributes)) {
    if (!(ATTRS as readonly string[]).includes(name)) {
      g.deleteAttribute(name);
    }
  }

  if (!g.getAttribute('normal')) {
    g.computeVertexNormals();
  }
  if (!g.getAttribute('color')) {
    g.setAttribute('color', new THREE.Float32BufferAttribute(new Float32Array(n * 3).fill(1), 3));
  }
  for (const mask of ['_team', '_glow']) {
    if (!g.getAttribute(mask)) {
      g.setAttribute(mask, new THREE.Float32BufferAttribute(new Float32Array(n), 1));
    }
  }

  fixNormals(g.getAttribute('normal') as THREE.BufferAttribute);
  return g;
}

/** Zero-length normals light as NaN, which the blur passes spread into white dots. */
function fixNormals(normal: THREE.BufferAttribute): void {
  for (let i = 0; i < normal.count; i++) {
    const x = normal.getX(i);
    const y = normal.getY(i);
    const z = normal.getZ(i);
    const len = Math.hypot(x, y, z);
    if (!(len > NORMAL_EPSILON)) {
      normal.setXYZ(i, 0, 1, 0);
      continue;
    }
    normal.setXYZ(i, x / len, y / len, z / len);
  }
  normal.needsUpdate = true;
}

/** Merge parts (already in the ATTRS layout) into one geometry, each moved, turned and scaled. */
export function compose(parts: readonly Part[]): THREE.BufferGeometry {
  const total = parts.reduce((sum, p) => sum + p.geometry.getAttribute('position').count, 0);
  const pos = new Float32Array(total * 3);
  const nor = new Float32Array(total * 3);
  const col = new Float32Array(total * 3);
  const team = new Float32Array(total);
  const glow = new Float32Array(total);

  let at = 0;
  for (const p of parts) {
    const g = p.geometry;
    const gp = g.getAttribute('position');
    const gn = g.getAttribute('normal');
    const gc = g.getAttribute('color');
    const gt = g.getAttribute('_team');
    const gg = g.getAttribute('_glow');
    const s = p.scale ?? 1;
    const c = Math.cos(p.yaw ?? 0);
    const sn = Math.sin(p.yaw ?? 0);
    for (let i = 0; i < gp.count; i++, at++) {
      // R_y(yaw): x' = c·x + s·z, z' = −s·x + c·z (writeMatrix in features/layer.ts).
      const x = gp.getX(i);
      const z = gp.getZ(i);
      pos[at * 3] = (c * x + sn * z) * s + (p.x ?? 0);
      pos[at * 3 + 1] = gp.getY(i) * s + (p.y ?? 0);
      pos[at * 3 + 2] = (-sn * x + c * z) * s + (p.z ?? 0);

      const nx = gn.getX(i);
      const nz = gn.getZ(i);
      nor[at * 3] = c * nx + sn * nz;
      nor[at * 3 + 1] = gn.getY(i);
      nor[at * 3 + 2] = -sn * nx + c * nz;

      col[at * 3] = gc.getX(i);
      col[at * 3 + 1] = gc.getY(i);
      col[at * 3 + 2] = gc.getZ(i);
      team[at] = p.mask ?? gt.getX(i);
      glow[at] = gg.getX(i);
    }
  }

  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  out.setAttribute('color', new THREE.BufferAttribute(col, 3));
  out.setAttribute('_team', new THREE.BufferAttribute(team, 1));
  out.setAttribute('_glow', new THREE.BufferAttribute(glow, 1));
  return out;
}

// ─── Ore heap ──────────────────────────────────────────────────────────────────────────────────

const HEAP_SIDES = 7;
const HEAP_SHADES = [0.95, 0.8, 0.9, 0.72, 0.86] as const;

interface Pile {
  x: number;
  z: number;
  radius: number;
  height: number;
}

/** Two piles: a main one and a smaller one beside it. */
const PILES: readonly Pile[] = [
  { x: 0, z: 0, radius: 0.15, height: 0.1 },
  { x: 0.13, z: 0.09, radius: 0.09, height: 0.06 },
];

/**
 * A low-poly pile of ore, flat shaded, base at y = 0, `_team` = 1 everywhere (the whole pile takes
 * the ore colour). Vertex colour is grey shading only.
 */
export function heapGeometry(): THREE.BufferGeometry {
  const pos: number[] = [];
  const col: number[] = [];
  for (const pile of PILES) {
    for (let k = 0; k < HEAP_SIDES; k++) {
      const a0 = (k / HEAP_SIDES) * Math.PI * 2;
      const a1 = ((k + 1) / HEAP_SIDES) * Math.PI * 2;
      const r0 = pile.radius * (0.82 + 0.3 * ((k * 7919) % 11) / 11);
      const r1 = pile.radius * (0.82 + 0.3 * (((k + 1) * 7919) % 11) / 11);
      const shade = HEAP_SHADES[k % HEAP_SHADES.length];
      const peak = pile.height * (0.85 + 0.3 * ((k * 31) % 7) / 7);

      // A face from the rim up to the crown.
      pos.push(pile.x + Math.cos(a0) * r0, 0, pile.z + Math.sin(a0) * r0);
      pos.push(pile.x, peak, pile.z);
      pos.push(pile.x + Math.cos(a1) * r1, 0, pile.z + Math.sin(a1) * r1);
      col.push(shade, shade, shade, 1, 1, 1, shade, shade, shade);
    }
  }

  const n = pos.length / 3;
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.setAttribute('_team', new THREE.Float32BufferAttribute(new Float32Array(n).fill(1), 1));
  g.setAttribute('_glow', new THREE.Float32BufferAttribute(new Float32Array(n), 1));
  g.computeVertexNormals();
  return g;
}

// ─── Footing ───────────────────────────────────────────────────────────────────────────────────

const FOOTING_SIDES = 10;
const FOOTING_SIDE_SHADE = 0.78;

/**
 * The slab a monument stands on: a unit prism, top face at y = 0 and bottom at y = -1, radius 1. An
 * instance scales it to the footprint and to the slab's depth. Vertex colour is shade only; the
 * instance's tint (the ground colour) supplies the hue.
 */
export function footingGeometry(): THREE.BufferGeometry {
  const pos: number[] = [];
  const col: number[] = [];
  const ring = (k: number): [number, number] => [Math.cos((k / FOOTING_SIDES) * Math.PI * 2), Math.sin((k / FOOTING_SIDES) * Math.PI * 2)];
  for (let k = 0; k < FOOTING_SIDES; k++) {
    const [x0, z0] = ring(k);
    const [x1, z1] = ring(k + 1);

    // Top face (up), then the side quad (outward), both counter-clockwise seen from outside.
    pos.push(0, 0, 0, x1, 0, z1, x0, 0, z0);
    col.push(1, 1, 1, 1, 1, 1, 1, 1, 1);
    pos.push(x0, 0, z0, x1, 0, z1, x1, -1, z1, x0, 0, z0, x1, -1, z1, x0, -1, z0);
    for (let i = 0; i < 6; i++) {
      col.push(FOOTING_SIDE_SHADE, FOOTING_SIDE_SHADE, FOOTING_SIDE_SHADE);
    }
  }

  const n = pos.length / 3;
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.setAttribute('_team', new THREE.Float32BufferAttribute(new Float32Array(n), 1));
  g.setAttribute('_glow', new THREE.Float32BufferAttribute(new Float32Array(n), 1));
  g.computeVertexNormals();
  return g;
}

// ─── Temple ────────────────────────────────────────────────────────────────────────────────────

const STONE: readonly [number, number, number] = [0.84, 0.82, 0.77];
const STONE_SHADOW: readonly [number, number, number] = [0.66, 0.63, 0.57];
const COLUMNS = 6;
const COLUMN_SIDES = 8;
const COLUMN_RADIUS = 0.011;

interface Face {
  pos: number[];
  col: number[];
  team: number[];
}

/** One triangle, wound so its front faces `toward` (a direction), shaded by `shade`. */
function tri(f: Face, a: readonly number[], b: readonly number[], c: readonly number[], toward: readonly number[], rgb: readonly number[], mask: number): void {
  const ux = b[0] - a[0];
  const uy = b[1] - a[1];
  const uz = b[2] - a[2];
  const vx = c[0] - a[0];
  const vy = c[1] - a[1];
  const vz = c[2] - a[2];
  const nx = uy * vz - uz * vy;
  const ny = uz * vx - ux * vz;
  const nz = ux * vy - uy * vx;
  const flip = nx * toward[0] + ny * toward[1] + nz * toward[2] < 0;
  const order = flip ? [a, c, b] : [a, b, c];
  for (const p of order) {
    f.pos.push(p[0], p[1], p[2]);
    f.col.push(rgb[0], rgb[1], rgb[2]);
    f.team.push(mask);
  }
}

/** An axis-aligned box from (x0, y0, z0) to (x1, y1, z1). */
function box(f: Face, x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, rgb: readonly number[], mask = 0): void {
  const p = (x: number, y: number, z: number): number[] => [x, y, z];
  const sides: Array<[number[], number[], number[], number[], number[]]> = [
    [p(x0, y1, z0), p(x1, y1, z0), p(x1, y1, z1), p(x0, y1, z1), [0, 1, 0]],
    [p(x0, y0, z0), p(x1, y0, z0), p(x1, y0, z1), p(x0, y0, z1), [0, -1, 0]],
    [p(x0, y0, z1), p(x1, y0, z1), p(x1, y1, z1), p(x0, y1, z1), [0, 0, 1]],
    [p(x0, y0, z0), p(x1, y0, z0), p(x1, y1, z0), p(x0, y1, z0), [0, 0, -1]],
    [p(x1, y0, z0), p(x1, y0, z1), p(x1, y1, z1), p(x1, y1, z0), [1, 0, 0]],
    [p(x0, y0, z0), p(x0, y0, z1), p(x0, y1, z1), p(x0, y1, z0), [-1, 0, 0]],
  ];
  for (const [a, b, c, d, toward] of sides) {
    const shade = toward[1] === 1 ? rgb : rgb.map((v) => v * (toward[1] === 0 && toward[0] === 0 ? 0.92 : 0.84));
    tri(f, a, b, c, toward, shade, mask);
    tri(f, a, c, d, toward, shade, mask);
  }
}

/**
 * A small Greek-style temple: two steps, a colonnade, a plain cella and a gabled roof (roof in the
 * tint mask). Base at y = 0, about 0.4 wide and 0.26 tall. A generic silhouette, not any real temple.
 */
export function templeGeometry(): THREE.BufferGeometry {
  const f: Face = { pos: [], col: [], team: [] };
  box(f, -0.2, 0, -0.13, 0.2, 0.022, 0.13, STONE);
  box(f, -0.185, 0.022, -0.115, 0.185, 0.044, 0.115, STONE);
  box(f, -0.135, 0.044, -0.07, 0.135, 0.17, 0.07, STONE_SHADOW);

  // Columns: two rows along the long sides.
  for (const z of [-0.098, 0.098]) {
    for (let i = 0; i < COLUMNS; i++) {
      const x = -0.15 + (i / (COLUMNS - 1)) * 0.3;
      for (let k = 0; k < COLUMN_SIDES; k++) {
        const a0 = (k / COLUMN_SIDES) * Math.PI * 2;
        const a1 = ((k + 1) / COLUMN_SIDES) * Math.PI * 2;
        const p0 = [x + Math.cos(a0) * COLUMN_RADIUS, 0.044, z + Math.sin(a0) * COLUMN_RADIUS];
        const p1 = [x + Math.cos(a1) * COLUMN_RADIUS, 0.044, z + Math.sin(a1) * COLUMN_RADIUS];
        const t0 = [p0[0], 0.17, p0[2]];
        const t1 = [p1[0], 0.17, p1[2]];
        const out = [Math.cos((a0 + a1) / 2), 0, Math.sin((a0 + a1) / 2)];
        tri(f, p0, p1, t1, out, STONE, 0);
        tri(f, p0, t1, t0, out, STONE, 0);
      }
    }
  }

  // Entablature and gabled roof.
  box(f, -0.19, 0.17, -0.12, 0.19, 0.19, 0.12, STONE);
  const apex = 0.255;
  const gable = (z: number): void => {
    tri(f, [-0.19, 0.19, z], [0.19, 0.19, z], [0, apex, z], [0, 0, Math.sign(z)], STONE, 0);
  };
  gable(-0.12);
  gable(0.12);
  for (const z of [-1, 1]) {
    const near = z * 0.12;
    const slope = [0, 0.12, z * 0.19];
    tri(f, [-0.19, 0.19, near], [0.19, 0.19, near], [0.19, apex, 0], slope, STONE, 1);
    tri(f, [-0.19, 0.19, near], [0.19, apex, 0], [-0.19, apex, 0], slope, STONE, 1);
  }

  const n = f.pos.length / 3;
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(f.pos, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(f.col, 3));
  g.setAttribute('_team', new THREE.Float32BufferAttribute(f.team, 1));
  g.setAttribute('_glow', new THREE.Float32BufferAttribute(new Float32Array(n), 1));
  g.computeVertexNormals();
  return g;
}

// ─── Colour ────────────────────────────────────────────────────────────────────────────────────

/** Vertex colours pulled toward their own luma: `keep` 1 leaves them, 0 turns the model grey. */
export function muted(source: THREE.BufferGeometry, keep: number): THREE.BufferGeometry {
  const g = source.clone();
  const col = g.getAttribute('color') as THREE.BufferAttribute;
  for (let i = 0; i < col.count; i++) {
    const luma = 0.2126 * col.getX(i) + 0.7152 * col.getY(i) + 0.0722 * col.getZ(i);
    col.setXYZ(i, luma + (col.getX(i) - luma) * keep, luma + (col.getY(i) - luma) * keep, luma + (col.getZ(i) - luma) * keep);
  }
  col.needsUpdate = true;
  return g;
}

// ─── Pads ──────────────────────────────────────────────────────────────────────────────────────

const PAD_MAX_Y = 0.04;
const PAD_MAX_LUMA = 0.1;

/**
 * The model without its dark ground slab: triangles all below PAD_MAX_Y and near black. Rigs then
 * stand straight on the terrain instead of on a dark disc.
 */
export function withoutPad(source: THREE.BufferGeometry): THREE.BufferGeometry {
  const pos = source.getAttribute('position');
  const col = source.getAttribute('color');
  const keep: number[] = [];
  for (let t = 0; t < pos.count; t += 3) {
    let low = true;
    let dark = true;
    for (let k = 0; k < 3; k++) {
      low = low && pos.getY(t + k) <= PAD_MAX_Y;
      dark = dark && 0.2126 * col.getX(t + k) + 0.7152 * col.getY(t + k) + 0.0722 * col.getZ(t + k) <= PAD_MAX_LUMA;
    }
    if (!(low && dark)) {
      keep.push(t, t + 1, t + 2);
    }
  }

  const out = new THREE.BufferGeometry();
  for (const name of ATTRS) {
    const from = source.getAttribute(name) as THREE.BufferAttribute;
    const size = from.itemSize;
    const data = new Float32Array(keep.length * size);
    keep.forEach((v, i) => {
      for (let c = 0; c < size; c++) {
        data[i * size + c] = from.getComponent(v, c);
      }
    });
    out.setAttribute(name, new THREE.BufferAttribute(data, size));
  }
  return out;
}
